import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TextField,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import SenderEmailMappings from '../../components/BusinessSettings/SenderEmailMappings';
import BusinessCleanupPanel from '../../components/BusinessSettings/BusinessCleanupPanel';
import AdminUiLabelsPanel from '../../components/BusinessSettings/AdminUiLabelsPanel';
import AssetViewsSettings from '../admin/settings/AssetViewsSettings';
import LocationViewsSettings from '../admin/settings/LocationViewsSettings';
import StorageUsagePanel from '../../components/BusinessSettings/StorageUsagePanel';
import PolicyMaxAiUsageCard from '../../components/PolicyMaxAiUsageCard';
import {
  DVLA_DAY_OF_MONTH_OPTIONS,
  DVLA_TIMEZONE_OPTIONS,
  formatScheduleSummary,
  formatScheduleTimeForInput,
} from '../../utils/dvlaSchedule';
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`business-settings-tabpanel-${index}`}
      aria-labelledby={`business-settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `business-settings-tab-${index}`,
    'aria-controls': `business-settings-tabpanel-${index}`,
  };
}

interface Business {
  id: number;
  business_id: string;
  business_name: string;
  business_email: string;
  phone_number: string;
  address: string;
  subscription_status: string;
  subscription_tier: string;
  max_users?: number;
  max_locations?: number;
  allow_custom_forms?: boolean;
  enable_api_access?: boolean;
  allow_template_creation?: boolean;
  allow_hr_module?: boolean;
  allow_loan_checks?: boolean;
  allow_safety_checks?: boolean;
  allow_delivery_checks?: boolean;
  allow_incidents?: boolean;
  allow_scan_sheets?: boolean;
  allow_dvla?: boolean;
  allow_mobile_dvla_checks?: boolean;
  allow_dvla_management_mobile?: boolean;
  disable_dvla_checks?: boolean;
  allow_assign_checks?: boolean;
  hide_templates?: boolean;
  allow_assets_management?: boolean | number;
  allow_installations?: boolean | number;
  allow_loan_return_ai_analysis?: boolean | number;
  allow_frequency_scheduling?: boolean | number;
  allow_ai_template_generation?: boolean | number;
  allow_mot_tax_tab?: boolean | number;
  allow_vehicle_evaluation_tab?: boolean | number;
  allow_calendar?: boolean | number;
  hide_mot_tax_nav_when_disabled?: boolean | number;
  hide_vehicle_evaluation_nav_when_disabled?: boolean | number;
  allow_shared_accounts?: boolean | number;
  allow_key_documents?: boolean | number;
  allow_location_checks?: boolean | number;
  allow_locations?: boolean | number;
  use_site_terminology?: boolean | number;
  ibcos_gold_wgs_enabled?: boolean | number;
  ibcos_gold_wgs_email?: string;
  created_at?: string;
  updated_at?: string;
  /** JSON map of stable keys → custom labels for web-admin sidebar/header */
  admin_ui_labels?: string | Record<string, string> | null;
  /** JSON map of quick access item keys → boolean visibility overrides */
  quick_access_config?: string | Record<string, boolean> | null;
}

const DEFAULT_QUICK_ACCESS_CONFIG = {
  new_check: true,
  incidents: true,
  assign_tasks: true,
  reassign_check: true,
  add_asset: true,
  deliveries: true,
  dvla_management: true,
  update_location: true,
  view_active_loans: true,
};

const BusinessSettings: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [autoSaveSuccess, setAutoSaveSuccess] = useState(false);
  const creditsSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Import history state
  const [importHistory, setImportHistory] = useState<any[]>([]);
  const [loadingImports, setLoadingImports] = useState(false);
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [selectedImport, setSelectedImport] = useState<any>(null);
  const [undoingImport, setUndoingImport] = useState(false);

  // Copy setup from another business (super admin / owner) - e.g. Check-Now Ltd (Demo)
  const DEMO_BUSINESS_NAME = 'Check-Now Ltd (Demo)';
  const [businessesList, setBusinessesList] = useState<{ business_id: string; business_name: string }[]>([]);
  const [sourceBusinessIdForCopy, setSourceBusinessIdForCopy] = useState('');
  const [sourceTemplatesForCopy, setSourceTemplatesForCopy] = useState<{ template_id: string; template_name: string; business_id?: string }[]>([]);
  const [sourceAssetTypesForCopy, setSourceAssetTypesForCopy] = useState<{ asset_type_id: string; type_name: string; business_id?: string; business_name?: string }[]>([]);
  const [selectedTemplateIdsForCopy, setSelectedTemplateIdsForCopy] = useState<string[]>([]);
  const [selectedAssetTypeIdsForCopy, setSelectedAssetTypeIdsForCopy] = useState<string[]>([]);
  const [loadingSourceDataForCopy, setLoadingSourceDataForCopy] = useState(false);
  const [setupFromSourceLoading, setSetupFromSourceLoading] = useState(false);
  const [setupFromSourceError, setSetupFromSourceError] = useState<string | null>(null);
  const [setupFromSourceSuccess, setSetupFromSourceSuccess] = useState(false);
  const [setupFromSourceCounts, setSetupFromSourceCounts] = useState<{ assetTypes: number; templates: number } | null>(null);
  const [loadingBusinessesForCopy, setLoadingBusinessesForCopy] = useState(false);
  
  // Form state for general settings
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  // Email configuration
  const [accountEmails, setAccountEmails] = useState('');
  const [overviewEmails, setOverviewEmails] = useState('');
  
  // Form state for subscription settings
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [subscriptionTier, setSubscriptionTier] = useState('');
  const [maxUsers, setMaxUsers] = useState(0);
  const [maxLocations, setMaxLocations] = useState(0);
  
  // Form state for feature settings
  const [allowCustomForms, setAllowCustomForms] = useState(false);
  const [enableApiAccess, setEnableApiAccess] = useState(false);
  const [allowTemplateCreation, setAllowTemplateCreation] = useState(true);
  const [allowHrModule, setAllowHrModule] = useState(true);
  const [allowLoanChecks, setAllowLoanChecks] = useState(true);
  const [allowSafetyChecks, setAllowSafetyChecks] = useState(true);
  const [allowDeliveryChecks, setAllowDeliveryChecks] = useState(true);
  const [allowIncidents, setAllowIncidents] = useState(true);
  const [allowScanSheets, setAllowScanSheets] = useState(true);
  const [allowDvla, setAllowDvla] = useState(true);
  const [allowMobileDvlaChecks, setAllowMobileDvlaChecks] = useState(false);
  const [allowDvlaManagementMobile, setAllowDvlaManagementMobile] = useState(true);
  const [disableDvlaChecks, setDisableDvlaChecks] = useState(false);
  const [allowAssignChecks, setAllowAssignChecks] = useState(true);
  const [hideTemplates, setHideTemplates] = useState(false);
  const [allowAssetsManagement, setAllowAssetsManagement] = useState(true);
  const [allowInstallations, setAllowInstallations] = useState(true);
  const [allowLoanReturnAiAnalysis, setAllowLoanReturnAiAnalysis] = useState(false);
  const [allowFrequencyScheduling, setAllowFrequencyScheduling] = useState(false);
  const [allowAiTemplateGeneration, setAllowAiTemplateGeneration] = useState(false);
  const [allowCalendar, setAllowCalendar] = useState(true);
  const [allowMotTaxTab, setAllowMotTaxTab] = useState(false);
  const [allowVehicleEvaluationTab, setAllowVehicleEvaluationTab] = useState(false);
  const [hideMotTaxNavWhenDisabled, setHideMotTaxNavWhenDisabled] = useState(false);
  const [hideVehicleEvalNavWhenDisabled, setHideVehicleEvalNavWhenDisabled] = useState(false);
  const [hideBothFleetNavWhenDisabled, setHideBothFleetNavWhenDisabled] = useState(false);
  const [allowSharedAccounts, setAllowSharedAccounts] = useState(false);
  const [allowKeyDocuments, setAllowKeyDocuments] = useState(true);
  const [allowLocationChecks, setAllowLocationChecks] = useState(false);
  const [allowLocations, setAllowLocations] = useState(true);
  const [useSiteTerminology, setUseSiteTerminology] = useState(false);

  // Quick Access Menu visibility config
  const [quickAccessConfig, setQuickAccessConfig] = useState<Record<string, boolean>>(DEFAULT_QUICK_ACCESS_CONFIG);

  const syncHideBothFleetNav = (hideMot: boolean, hideEval: boolean) => {
    setHideBothFleetNavWhenDisabled(hideMot && hideEval);
  };

  // Integrations
  const [ibcosGoldWgsEnabled, setIbcosGoldWgsEnabled] = useState(false);
  const [ibcosGoldWgsEmail, setIbcosGoldWgsEmail] = useState('');
  const [dvlaEnvironment, setDvlaEnvironment] = useState<'test' | 'live'>('live');
  const [dvlaCredits, setDvlaCredits] = useState(0);
  const [pendingCredits, setPendingCredits] = useState(0);
  const [dvlaCheckFrequency, setDvlaCheckFrequency] = useState<string>('');
  const [dvlaScheduleDayOfMonth, setDvlaScheduleDayOfMonth] = useState<number>(1);
  const [dvlaScheduleTime, setDvlaScheduleTime] = useState('06:00');
  const [dvlaScheduleTimezone, setDvlaScheduleTimezone] = useState('Europe/London');
  const [nextDvlaCheckDate, setNextDvlaCheckDate] = useState<string | null>(null);
  const [dvlaAutomatedCheckEmail, setDvlaAutomatedCheckEmail] = useState('');

  // Status and tier options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const tierOptions = [
    { value: 'basic', label: 'Basic' },
    { value: 'standard', label: 'Standard' },
    { value: 'premium', label: 'Premium' },
    { value: 'enterprise', label: 'Enterprise' },
  ];

  const fetchBusinessDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Business details response:', response.data);
      const businessData = response.data;
      setBusiness(businessData);
      
      // Set form values
      setBusinessName(businessData.business_name || '');
      setBusinessEmail(businessData.business_email || '');
      setPhoneNumber(businessData.phone_number || '');
      setAddress(businessData.address || '');
      setSubscriptionStatus(businessData.subscription_status || '');
      setSubscriptionTier(businessData.subscription_tier || '');
      setAccountEmails(businessData.account_emails || '');
      setOverviewEmails(businessData.overview_emails || '');
      setMaxUsers(businessData.max_users || 5);
      setMaxLocations(businessData.max_locations || 1);
      setAllowCustomForms(businessData.allow_custom_forms === 1);
      setEnableApiAccess(businessData.enable_api_access === 1);
      setAllowTemplateCreation(businessData.allow_template_creation === 1);
      setAllowHrModule(businessData.allow_hr_module === 1);
      setAllowLoanChecks(businessData.allow_loan_checks === 1);
      setAllowSafetyChecks(businessData.allow_safety_checks === 1);
      setAllowDeliveryChecks(businessData.allow_delivery_checks !== 0);
      setAllowIncidents(businessData.allow_incidents === 1);
      setAllowScanSheets(businessData.allow_scan_sheets === 1);
      setAllowDvla(businessData.allow_dvla === 1);
      setAllowMobileDvlaChecks(businessData.allow_mobile_dvla_checks === 1);
      setAllowDvlaManagementMobile(businessData.allow_dvla_management_mobile !== 0);
      setDisableDvlaChecks(businessData.disable_dvla_checks === 1);
      setAllowAssignChecks(businessData.allow_assign_checks !== 0);
      setHideTemplates(businessData.hide_templates === 1 || businessData.hide_templates === true);
      setAllowAssetsManagement(businessData.allow_assets_management !== 0);
      setAllowInstallations(businessData.allow_installations !== 0);
      setAllowLoanReturnAiAnalysis(businessData.allow_loan_return_ai_analysis === 1 || businessData.allow_loan_return_ai_analysis === true);
      setAllowFrequencyScheduling(businessData.allow_frequency_scheduling === 1 || businessData.allow_frequency_scheduling === true);
      setAllowAiTemplateGeneration(businessData.allow_ai_template_generation === 1 || businessData.allow_ai_template_generation === true);
      setAllowCalendar(
        businessData.allow_calendar == null
          ? true
          : businessData.allow_calendar === 1 || businessData.allow_calendar === true
      );
      setAllowMotTaxTab(businessData.allow_mot_tax_tab === 1 || businessData.allow_mot_tax_tab === true);
      setAllowVehicleEvaluationTab(businessData.allow_vehicle_evaluation_tab === 1 || businessData.allow_vehicle_evaluation_tab === true);
      const hideMot =
        businessData.hide_mot_tax_nav_when_disabled === 1 || businessData.hide_mot_tax_nav_when_disabled === true;
      const hideEval =
        businessData.hide_vehicle_evaluation_nav_when_disabled === 1 ||
        businessData.hide_vehicle_evaluation_nav_when_disabled === true;
      setHideMotTaxNavWhenDisabled(hideMot);
      setHideVehicleEvalNavWhenDisabled(hideEval);
      setHideBothFleetNavWhenDisabled(hideMot && hideEval);
      setAllowSharedAccounts(businessData.allow_shared_accounts === 1 || businessData.allow_shared_accounts === true);
      setAllowKeyDocuments(
        businessData.allow_key_documents == null
          ? true
          : businessData.allow_key_documents === 1 || businessData.allow_key_documents === true
      );
      setAllowLocationChecks(businessData.allow_location_checks === 1 || businessData.allow_location_checks === true);
      setAllowLocations(businessData.allow_locations !== 0 && businessData.allow_locations !== false);
      setUseSiteTerminology(businessData.use_site_terminology === 1 || businessData.use_site_terminology === true);

      // Quick Access Menu config
      try {
        const rawQA = businessData.quick_access_config;
        const parsedQA = rawQA
          ? (typeof rawQA === 'string' ? JSON.parse(rawQA) : rawQA)
          : {};
        setQuickAccessConfig({ ...DEFAULT_QUICK_ACCESS_CONFIG, ...(typeof parsedQA === 'object' ? parsedQA : {}) });
      } catch {
        setQuickAccessConfig(DEFAULT_QUICK_ACCESS_CONFIG);
      }

      // Integrations
      setIbcosGoldWgsEnabled(
        businessData.ibcos_gold_wgs_enabled === 1 ||
        businessData.ibcos_gold_wgs_enabled === true
      );
      setIbcosGoldWgsEmail(businessData.ibcos_gold_wgs_email || '');
      setDvlaEnvironment(businessData.dvla_environment || 'live');
      setDvlaCredits(businessData.dvla_credits || 0);
      setDvlaCheckFrequency(businessData.dvla_check_frequency || '');
      setDvlaScheduleDayOfMonth(
        businessData.dvla_schedule_day_of_month != null
          ? Number(businessData.dvla_schedule_day_of_month)
          : 1
      );
      setDvlaScheduleTime(formatScheduleTimeForInput(businessData.dvla_schedule_time));
      setDvlaScheduleTimezone(businessData.dvla_schedule_timezone || 'Europe/London');
      setNextDvlaCheckDate(businessData.next_dvla_check_date || null);
      setDvlaAutomatedCheckEmail(businessData.dvla_automated_check_email || '');
      
      // Fetch pending requests to calculate reserved credits
      try {
        const token = localStorage.getItem('token');
        const pendingResponse = await axios.get(`${API_BASE_URL}/dvla-license-requests?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingCredits(pendingResponse.data.length || 0);
      } catch (err) {
        console.error('Error fetching pending requests:', err);
      }
      
    } catch (err: any) {
      console.error('Error fetching business details:', err);
      setError('Failed to fetch business details. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [businessId, navigate]);

  useEffect(() => {
    if (businessId) {
      fetchBusinessDetails();
    }
  }, [businessId, fetchBusinessDetails]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (creditsSaveTimeoutRef.current) {
        clearTimeout(creditsSaveTimeoutRef.current);
      }
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Load import history when switching to Import History tab
    if (newValue === 5) {
      fetchImportHistory();
    }
    // Load businesses list when switching to Copy setup tab
    if (newValue === 6) {
      fetchBusinessesForCopy();
    }
  };

  const fetchBusinessesForCopy = async () => {
    setLoadingBusinessesForCopy(true);
    setSetupFromSourceError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get<{ business_id: string; business_name: string }[]>(`${API_BASE_URL}/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setBusinessesList(list);
      const demo = list.find((b) => b.business_name === DEMO_BUSINESS_NAME);
      if (demo && demo.business_id !== businessId) setSourceBusinessIdForCopy(demo.business_id);
    } catch (err: any) {
      setSetupFromSourceError('Failed to load businesses list.');
    } finally {
      setLoadingBusinessesForCopy(false);
    }
  };

  // When Copy setup source business changes, load its templates and asset types (for tick-to-copy)
  useEffect(() => {
    if (tabValue !== 6 || !sourceBusinessIdForCopy || sourceBusinessIdForCopy === businessId) {
      setSourceTemplatesForCopy([]);
      setSourceAssetTypesForCopy([]);
      setSelectedTemplateIdsForCopy([]);
      setSelectedAssetTypeIdsForCopy([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoadingSourceDataForCopy(true);
    Promise.all([
      axios.get<{ template_id: string; template_name: string; business_id?: string }[]>(`${API_BASE_URL}/check-templates/all-businesses`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get<{ data: { asset_type_id: string; type_name: string; business_id?: string; business_name?: string }[] }>(`${API_BASE_URL}/asset-types/all-businesses`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([templatesRes, assetTypesRes]) => {
        const allTemplates = Array.isArray(templatesRes.data) ? templatesRes.data : [];
        const rawAssetTypes = assetTypesRes.data?.data ?? assetTypesRes.data;
        const allAssetTypes = Array.isArray(rawAssetTypes) ? rawAssetTypes : [];
        const templates = allTemplates.filter((t) => t.business_id === sourceBusinessIdForCopy);
        const assetTypes = allAssetTypes.filter((a) => a.business_id === sourceBusinessIdForCopy);
        setSourceTemplatesForCopy(templates);
        setSourceAssetTypesForCopy(assetTypes);
        setSelectedTemplateIdsForCopy([]);
        setSelectedAssetTypeIdsForCopy([]);
      })
      .catch(() => {
        setSourceTemplatesForCopy([]);
        setSourceAssetTypesForCopy([]);
      })
      .finally(() => setLoadingSourceDataForCopy(false));
  }, [tabValue, sourceBusinessIdForCopy, businessId, businessesList]);

  const handleCopyFromSource = async () => {
    if (!businessId || !sourceBusinessIdForCopy) {
      setSetupFromSourceError('Select a source business.');
      return;
    }
    if (selectedTemplateIdsForCopy.length === 0 && selectedAssetTypeIdsForCopy.length === 0) {
      setSetupFromSourceError('Select at least one template or asset type to copy.');
      return;
    }
    setSetupFromSourceLoading(true);
    setSetupFromSourceError(null);
    setSetupFromSourceSuccess(false);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await axios.post<{ success: boolean; copied_asset_types?: number; copied_templates?: number }>(
        `${API_BASE_URL}/businesses/${businessId}/setup-from-source`,
        {
          source_business_id: sourceBusinessIdForCopy,
          template_ids: selectedTemplateIdsForCopy,
          asset_type_ids: selectedAssetTypeIdsForCopy,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const at = response.data?.copied_asset_types ?? 0;
      const tmpl = response.data?.copied_templates ?? 0;
      setSetupFromSourceCounts({ assetTypes: at, templates: tmpl });
      setSetupFromSourceSuccess(true);
      setTimeout(() => {
        setSetupFromSourceSuccess(false);
        setSetupFromSourceCounts(null);
      }, 5000);
      setSelectedTemplateIdsForCopy([]);
      setSelectedAssetTypeIdsForCopy([]);
    } catch (err: any) {
      setSetupFromSourceError(err.response?.data?.message || 'Failed to copy setup from source. Only Super Admins can copy to other businesses; Owners can copy to their own business only.');
    } finally {
      setSetupFromSourceLoading(false);
    }
  };

  const fetchImportHistory = async () => {
    if (!businessId) return;
    
    setLoadingImports(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/assets/imports/history/${businessId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setImportHistory(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching import history:', err);
      setError('Failed to load import history');
    } finally {
      setLoadingImports(false);
    }
  };

  const handleUndoImport = (importRecord: any) => {
    setSelectedImport(importRecord);
    setUndoDialogOpen(true);
  };

  const confirmUndoImport = async () => {
    if (!selectedImport) return;
    
    setUndoingImport(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/assets/imports/undo/${selectedImport.import_id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSaveSuccess(true);
      setError(null);
      setUndoDialogOpen(false);
      setSelectedImport(null);
      
      // Refresh import history
      fetchImportHistory();
      
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error undoing import:', err);
      setError(err.response?.data?.message || 'Failed to undo import');
      setUndoDialogOpen(false);
      setSelectedImport(null);
    } finally {
      setUndoingImport(false);
    }
  };
  
  const handleSaveGeneralSettings = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      setSaveSuccess(false);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      await axios.put(
        `${API_BASE_URL}/businesses/${businessId}`, 
        {
          business_name: businessName,
          business_email: businessEmail,
          phone_number: phoneNumber,
          address: address,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving general settings:', err);
      setError('Failed to save general settings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveSubscriptionSettings = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      setSaveSuccess(false);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/subscription`, 
        {
          subscription_status: subscriptionStatus,
          subscription_tier: subscriptionTier,
          max_users: maxUsers,
          max_locations: maxLocations,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving subscription settings:', err);
      setError('Failed to save subscription settings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveFeatureSettings = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      setSaveSuccess(false);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/features`, 
        {
          allow_custom_forms: allowCustomForms,
          enable_api_access: enableApiAccess,
          allow_template_creation: allowTemplateCreation,
          allow_hr_module: allowHrModule,
          allow_loan_checks: allowLoanChecks,
          allow_safety_checks: allowSafetyChecks,
          allow_delivery_checks: allowDeliveryChecks,
                  allow_incidents: allowIncidents,
        allow_scan_sheets: allowScanSheets,
        allow_dvla: allowDvla,
        allow_mobile_dvla_checks: allowMobileDvlaChecks,
        allow_dvla_management_mobile: allowDvlaManagementMobile,
        disable_dvla_checks: disableDvlaChecks,
        allow_assign_checks: allowAssignChecks,
        hide_templates: hideTemplates,
        allow_assets_management: allowAssetsManagement,
        allow_installations: allowInstallations,
        allow_loan_return_ai_analysis: allowLoanReturnAiAnalysis,
        allow_frequency_scheduling: allowFrequencyScheduling,
        allow_ai_template_generation: allowAiTemplateGeneration,
        allow_calendar: allowCalendar,
        allow_mot_tax_tab: allowMotTaxTab,
        allow_vehicle_evaluation_tab: allowVehicleEvaluationTab,
        hide_mot_tax_nav_when_disabled: hideMotTaxNavWhenDisabled,
        hide_vehicle_evaluation_nav_when_disabled: hideVehicleEvalNavWhenDisabled,
        allow_shared_accounts: allowSharedAccounts,
        allow_key_documents: allowKeyDocuments,
        allow_location_checks: allowLocationChecks,
        allow_locations: allowLocations,
        use_site_terminology: useSiteTerminology,
        quick_access_config: quickAccessConfig,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving feature settings:', err);
      setError('Failed to save feature settings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const buildDvlaIntegrationPayload = useCallback(
    (overrides: { dvla_credits?: number } = {}) => ({
      ibcos_gold_wgs_enabled: ibcosGoldWgsEnabled,
      ibcos_gold_wgs_email: ibcosGoldWgsEnabled ? ibcosGoldWgsEmail.trim() : null,
      dvla_environment: dvlaEnvironment,
      dvla_credits: overrides.dvla_credits ?? dvlaCredits,
      dvla_check_frequency: dvlaCheckFrequency || null,
      dvla_schedule_day_of_month: dvlaCheckFrequency ? dvlaScheduleDayOfMonth : null,
      dvla_schedule_time: dvlaCheckFrequency ? `${dvlaScheduleTime}:00` : null,
      dvla_schedule_timezone: dvlaCheckFrequency ? dvlaScheduleTimezone : null,
      dvla_automated_check_email: dvlaAutomatedCheckEmail.trim() || null,
    }),
    [
      ibcosGoldWgsEnabled,
      ibcosGoldWgsEmail,
      dvlaEnvironment,
      dvlaCredits,
      dvlaCheckFrequency,
      dvlaScheduleDayOfMonth,
      dvlaScheduleTime,
      dvlaScheduleTimezone,
      dvlaAutomatedCheckEmail,
    ]
  );

  // Auto-save DVLA credits with debouncing
  const autoSaveDvlaCredits = useCallback(async (newCreditAmount: number) => {
    if (!businessId) return;
    
    try {
      setAutoSaving(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/integrations`,
        buildDvlaIntegrationPayload({ dvla_credits: newCreditAmount }),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setAutoSaveSuccess(true);
      setTimeout(() => setAutoSaveSuccess(false), 2000);
    } catch (err: any) {
      console.error('Error auto-saving DVLA credits:', err);
      setError(err.response?.data?.message || 'Failed to save credits. Please try again later.');
    } finally {
      setAutoSaving(false);
    }
  }, [businessId, buildDvlaIntegrationPayload, navigate]);

  // Debounced auto-save for text input changes
  const debouncedAutoSaveDvlaCredits = useCallback((newCreditAmount: number) => {
    if (creditsSaveTimeoutRef.current) {
      clearTimeout(creditsSaveTimeoutRef.current);
    }
    
    creditsSaveTimeoutRef.current = setTimeout(() => {
      autoSaveDvlaCredits(newCreditAmount);
    }, 1000); // Wait 1 second after user stops typing
  }, [autoSaveDvlaCredits]);

  // Immediate auto-save for button clicks
  const handleCreditButtonClick = useCallback((adjustment: number) => {
    const newAmount = Math.max(0, dvlaCredits + adjustment);
    setDvlaCredits(newAmount);
    autoSaveDvlaCredits(newAmount);
  }, [dvlaCredits, autoSaveDvlaCredits]);

  // Handle manual credit input changes
  const handleCreditInputChange = useCallback((newAmount: number) => {
    setDvlaCredits(newAmount);
    debouncedAutoSaveDvlaCredits(newAmount);
  }, [debouncedAutoSaveDvlaCredits]);

  const handleSaveIntegrationSettings = async () => {
    if (!businessId) return;
    if (ibcosGoldWgsEnabled && !ibcosGoldWgsEmail.trim()) {
      setError('Please provide an email address for the IBCOS integration.');
      return;
    }
    try {
      setLoading(true);
      setSaveSuccess(false);

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/integrations`,
        buildDvlaIntegrationPayload(),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      await fetchBusinessDetails();
      setSaveSuccess(true);
      setError(null);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving integration settings:', err);
      setError(err.response?.data?.message || 'Failed to save integration settings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !business) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', p: 3 }}> 
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Business Settings
        </Typography>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/owner/business-management')}
        >
          Back to Business Management
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}
      
      {business && (
        <Paper sx={{ width: '100%', mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              aria-label="business settings tabs"
              variant="fullWidth"
            >
              <Tab label="General Information" {...a11yProps(0)} />
              <Tab label="Subscription & Limits" {...a11yProps(1)} />
              <Tab label="Features & Access" {...a11yProps(2)} />
              <Tab label="Email Configuration" {...a11yProps(3)} />
              <Tab label="Integrations" {...a11yProps(4)} />
              <Tab label="Import History" {...a11yProps(5)} />
              <Tab label="Copy setup" {...a11yProps(6)} />
              <Tab label="Business Cleanup" {...a11yProps(7)} />
              <Tab label="Admin UI labels" {...a11yProps(8)} />
              <Tab label="Storage Usage" {...a11yProps(9)} />
              <Tab label="Asset Views" {...a11yProps(10)} />
              <Tab label="Location Views" {...a11yProps(11)} />
            </Tabs>
          </Box>
          
          {/* General Information Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              General Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business Email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  margin="normal"
                  type="email"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveGeneralSettings}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Subscription & Limits Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>
              Subscription & Limits
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="status-label">Subscription Status</InputLabel>
                  <Select
                    labelId="status-label"
                    value={subscriptionStatus}
                    label="Subscription Status"
                    onChange={(e) => setSubscriptionStatus(e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="tier-label">Subscription Tier</InputLabel>
                  <Select
                    labelId="tier-label"
                    value={subscriptionTier}
                    label="Subscription Tier"
                    onChange={(e) => setSubscriptionTier(e.target.value)}
                  >
                    {tierOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                  Resource Limits
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Users"
                  type="number"
                  value={maxUsers}
                  onChange={(e) => setMaxUsers(parseInt(e.target.value) || 0)}
                  margin="normal"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Locations"
                  type="number"
                  value={maxLocations}
                  onChange={(e) => setMaxLocations(parseInt(e.target.value) || 0)}
                  margin="normal"
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveSubscriptionSettings}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Features & Access Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Features & Access
            </Typography>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Template Creation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to create new templates
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowTemplateCreation}
                        onChange={(e) => setAllowTemplateCreation(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      HR Module
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to use the HR module
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowHrModule}
                        onChange={(e) => setAllowHrModule(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      DISABLE DVLA checks (Mobile)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, mobile app will only store the license number and skip calling DVLA.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={disableDvlaChecks}
                        onChange={(e) => setDisableDvlaChecks(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Assign Checks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When disabled, hides New Check, Assign Tasks, and Re-assign Check in the mobile quick access menu.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowAssignChecks}
                        onChange={(e) => setAllowAssignChecks(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Hide Templates (Web)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, the Templates section is hidden from the admin sidebar.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={hideTemplates}
                        onChange={(e) => setHideTemplates(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Loan Checks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to perform loan checks and tracking
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowLoanChecks}
                        onChange={(e) => setAllowLoanChecks(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Safety Checks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to perform safety checks and inspections
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowSafetyChecks}
                        onChange={(e) => setAllowSafetyChecks(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Delivery Checks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to create delivery checks for asset transfers
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowDeliveryChecks}
                        onChange={(e) => setAllowDeliveryChecks(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Sites / Locations Module
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable the Sites / Locations module — allows managing locations and enables location-based scoping in Communications (schedules, round-ups and alert rules)
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowLocations}
                        onChange={(e) => setAllowLocations(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Location Checks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to run location checks (risk assessments etc.) with auto-stored PDFs and AI pass/fail analysis
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowLocationChecks}
                        onChange={(e) => setAllowLocationChecks(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Use "Site" Terminology
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Show "Site" instead of "Location" across the admin portal and mobile app
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={useSiteTerminology}
                        onChange={(e) => setUseSiteTerminology(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Incident Reporting
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to create and manage incident reports
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowIncidents}
                        onChange={(e) => setAllowIncidents(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Asset Management (Mobile)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Show asset management section in the mobile app More screen
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowAssetsManagement}
                        onChange={(e) => setAllowAssetsManagement(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Key Documents &amp; RAMS
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Per-asset compliance vault: LOLER, PUWER, RAMS, insurance, folders, text documents, AI analysis, and auto-stored check PDFs
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowKeyDocuments}
                        onChange={(e) => setAllowKeyDocuments(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Installations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable installations feature for outgoing deliveries
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowInstallations}
                        onChange={(e) => setAllowInstallations(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Scan Paper Sheets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow this business to scan and process paper check sheets
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowScanSheets}
                        onChange={(e) => setAllowScanSheets(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Enable Web-Admin DVLA Checks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toggle access to DVLA checks within the Admin dashboard (web). This does not affect Mobile DVLA checks.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowDvla}
                        onChange={(e) => setAllowDvla(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Mobile Loan DVLA Checks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable an extra DVLA screen in the mobile Loan Agreement journey to collect a driving license number
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowMobileDvlaChecks}
                        onChange={(e) => setAllowMobileDvlaChecks(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      DVLA Management Mobile App
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable the DVLA Management feature in the mobile app (staff management, analytics, settings)
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowDvlaManagementMobile}
                        onChange={(e) => setAllowDvlaManagementMobile(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Loan Return AI Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable AI analysis for loan return checks. When enabled, AI will compare initial loan check answers with return check answers to automatically detect condition deterioration and mark assets out of action if needed.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowLoanReturnAiAnalysis}
                        onChange={(e) => setAllowLoanReturnAiAnalysis(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Frequency &amp; Scheduling (Template Builder)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, the Frequency &amp; Scheduling section (interval checking and scheduled alerts) is shown in the Check Template Builder. When disabled, it is hidden for this business.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowFrequencyScheduling}
                        onChange={(e) => setAllowFrequencyScheduling(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Generate with AI (Template Builder)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, the "Generate with AI" button is shown in the Template Builder, allowing users to generate check templates using AI. When disabled, the button is greyed out.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={allowAiTemplateGeneration}
                        onChange={(e) => setAllowAiTemplateGeneration(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Booking calendar
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, this business gets the Calendar section in web admin, public booking page, and the
                      Calendar tab in the mobile app. When disabled, calendar and bookings are hidden for all users.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowCalendar}
                        onChange={(e) => setAllowCalendar(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Fleet sidebar visibility
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose whether disabled fleet features appear greyed out in the web admin sidebar, or are hidden
                  completely. You can apply this to both MOT / Tax and Vehicle Evaluation at once, or configure each
                  feature separately below.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="medium">
                      Hide both when disabled
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Removes MOT / Tax and Vehicle Evaluation from the sidebar when those features are turned off
                      (instead of showing them greyed out).
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideBothFleetNavWhenDisabled}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setHideBothFleetNavWhenDisabled(on);
                          setHideMotTaxNavWhenDisabled(on);
                          setHideVehicleEvalNavWhenDisabled(on);
                        }}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      MOT / Tax (Fleet)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, the MOT / Tax section is available in the web admin Fleet menu and MOT actions
                      appear in Asset Management.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowMotTaxTab}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setAllowMotTaxTab(on);
                          const nextHideMot = on ? false : hideMotTaxNavWhenDisabled;
                          if (on) setHideMotTaxNavWhenDisabled(false);
                          syncHideBothFleetNav(nextHideMot, hideVehicleEvalNavWhenDisabled);
                        }}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="medium">
                      Hide MOT / Tax from sidebar when disabled
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {allowMotTaxTab
                        ? 'Turn off “MOT / Tax (Fleet)” above to use this option.'
                        : 'When off, admins see a greyed-out MOT / Tax link. When on, the sidebar entry is removed.'}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideMotTaxNavWhenDisabled}
                        disabled={allowMotTaxTab}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setHideMotTaxNavWhenDisabled(on);
                          syncHideBothFleetNav(on, hideVehicleEvalNavWhenDisabled);
                        }}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Shared Accounts
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, admins see Shared Accounts under System Settings and users can sign in with a shared account on the mobile app. Off by default.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowSharedAccounts}
                        onChange={(e) => setAllowSharedAccounts(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Vehicle Evaluation (Fleet)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, the Vehicle Evaluation section is available in the web admin Fleet menu.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowVehicleEvaluationTab}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setAllowVehicleEvaluationTab(on);
                          const nextHideEval = on ? false : hideVehicleEvalNavWhenDisabled;
                          if (on) setHideVehicleEvalNavWhenDisabled(false);
                          syncHideBothFleetNav(hideMotTaxNavWhenDisabled, nextHideEval);
                        }}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="medium">
                      Hide Vehicle Evaluation from sidebar when disabled
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {allowVehicleEvaluationTab
                        ? 'Turn off “Vehicle Evaluation (Fleet)” above to use this option.'
                        : 'When off, admins see a greyed-out link. When on, the sidebar entry is removed.'}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideVehicleEvalNavWhenDisabled}
                        disabled={allowVehicleEvaluationTab}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setHideVehicleEvalNavWhenDisabled(on);
                          syncHideBothFleetNav(hideMotTaxNavWhenDisabled, on);
                        }}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>
            
            {/* Quick Access Menu Section */}
            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Quick Access Menu (Mobile Dashboard)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose which items appear in the Quick Access section on the mobile dashboard.
                  Items are also subject to the feature toggles above — if a feature is disabled,
                  its Quick Access button will be hidden regardless of this setting.
                </Typography>
                {[
                  { key: 'new_check', label: 'New Check', note: 'Requires Assign Checks to be enabled' },
                  { key: 'incidents', label: 'Incidents', note: 'Requires Incident Reporting to be enabled' },
                  { key: 'assign_tasks', label: 'Assign Tasks', note: 'Requires Assign Checks to be enabled' },
                  { key: 'reassign_check', label: 'Reassign Check', note: 'Requires Assign Checks to be enabled' },
                  { key: 'add_asset', label: 'Add Asset', note: 'Always visible when shown' },
                  { key: 'deliveries', label: 'Deliveries', note: 'Requires Delivery Checks to be enabled' },
                  { key: 'dvla_management', label: 'DVLA Management', note: 'Requires DVLA Management Mobile App to be enabled' },
                  { key: 'update_location', label: 'Update Location', note: 'Always visible when shown' },
                  { key: 'view_active_loans', label: 'View Active Loans', note: 'Requires Loan Checks to be enabled' },
                ].map(({ key, label, note }, idx, arr) => (
                  <Box key={key}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">{label}</Typography>
                        <Typography variant="body2" color="text.secondary">{note}</Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={quickAccessConfig[key] !== false}
                            onChange={(e) =>
                              setQuickAccessConfig((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                            color="primary"
                          />
                        }
                        label=""
                      />
                    </Box>
                    {idx < arr.length - 1 && <Divider />}
                  </Box>
                ))}
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveFeatureSettings}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" gutterBottom>
              Integrations
            </Typography>

            {businessId && (
              <PolicyMaxAiUsageCard
                businessId={businessId}
                businessName={business?.business_name}
                refreshKey={tabValue}
              />
            )}

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      IBCOS Gold WGS Doc Store Integration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toggle integration with IBCOS Gold document store for Wholegood Number (WGS) assets.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ibcosGoldWgsEnabled}
                        onChange={(e) => setIbcosGoldWgsEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>

                {ibcosGoldWgsEnabled && (
                  <Box sx={{ mt: 1 }}>
                    <TextField
                      fullWidth
                      label="Notification / Integration Email"
                      type="email"
                      value={ibcosGoldWgsEmail}
                      onChange={(e) => setIbcosGoldWgsEmail(e.target.value)}
                      placeholder="e.g. ibcos-docs@example.com"
                      helperText="Email address used for IBCOS Gold WGS Doc Store notifications or integration."
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  DVLA Environment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure which DVLA API environment to use for staff DVLA checks. Test mode uses the UAT environment, while Live mode uses the production API.
                </Typography>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>DVLA Environment</InputLabel>
                  <Select
                    value={dvlaEnvironment}
                    onChange={(e) => setDvlaEnvironment(e.target.value as 'test' | 'live')}
                    label="DVLA Environment"
                  >
                    <MenuItem value="live">Live (Production)</MenuItem>
                    <MenuItem value="test">Test (UAT)</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  DVLA Credit Management
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Manage DVLA check credits for this business. Each DVLA check consumes one credit. Monitor usage and add/remove credits as needed.
                </Typography>
                
                {/* Current Credits Display */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  bgcolor: 'primary.light', 
                  borderRadius: 1, 
                  mb: 3,
                  color: 'primary.contrastText',
                  position: 'relative'
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" fontWeight="bold">
                      {dvlaCredits}
                    </Typography>
                    <Typography variant="body2">
                      Total Credits
                    </Typography>
                    {pendingCredits > 0 && (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'warning.light' }}>
                          {pendingCredits} pending (reserved)
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'success.light' }}>
                          {dvlaCredits - pendingCredits} available
                        </Typography>
                      </>
                    )}
                    {pendingCredits === 0 && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {dvlaCredits} available
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ 
                    bgcolor: dvlaCredits > 10 ? 'success.main' : dvlaCredits > 5 ? 'warning.main' : 'error.main',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    color: 'white'
                  }}>
                    <Typography variant="body2" fontWeight="medium">
                      {dvlaCredits === 0 ? 'No Credits' : 
                       dvlaCredits <= 5 ? 'Low Credits' : 
                       dvlaCredits <= 10 ? 'Medium Credits' : 
                       'Good Credits'}
                    </Typography>
                  </Box>
                  {/* Auto-save indicator */}
                  {autoSaving && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8, 
                      display: 'flex', 
                      alignItems: 'center',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      color: 'primary.main'
                    }}>
                      <CircularProgress size={12} sx={{ mr: 0.5 }} />
                      <Typography variant="caption">Saving...</Typography>
                    </Box>
                  )}
                </Box>

                {/* Credit Management Controls */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Set Credits"
                      value={dvlaCredits}
                      onChange={(e) => handleCreditInputChange(parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 10000 }}
                      helperText="Auto-saves 1 second after you stop typing"
                      variant="outlined"
                      disabled={autoSaving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'flex-start', pt: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleCreditButtonClick(10)}
                        sx={{ minWidth: 'auto', px: 1 }}
                        disabled={autoSaving}
                      >
                        +10
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleCreditButtonClick(25)}
                        sx={{ minWidth: 'auto', px: 1 }}
                        disabled={autoSaving}
                      >
                        +25
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleCreditButtonClick(50)}
                        sx={{ minWidth: 'auto', px: 1 }}
                        disabled={autoSaving}
                      >
                        +50
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleCreditButtonClick(-10)}
                        sx={{ minWidth: 'auto', px: 1 }}
                        disabled={autoSaving}
                      >
                        -10
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                {/* Usage Information */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Credit Usage & Auto-Save Information:</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Each manual DVLA check consumes 1 credit<br/>
                    • Each automated DVLA check consumes 1 credit per staff member<br/>
                    • Credits are deducted immediately when checks are performed<br/>
                    • Set to 0 to disable DVLA checks for this business<br/>
                    • <strong>Quick buttons (+10, +25, +50, -10) save immediately</strong><br/>
                    • <strong>Manual input auto-saves 1 second after you stop typing</strong>
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Automated Check Frequency
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure when automated DVLA checks run. Choose how often, which day of the month, and what time
                  (e.g. 1st of every month at 15:00). Alerts are delivered via Communications → Alerts.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Check Frequency</InputLabel>
                      <Select
                        value={dvlaCheckFrequency}
                        onChange={(e) => setDvlaCheckFrequency(e.target.value)}
                        label="Check Frequency"
                      >
                        <MenuItem value="">Not Set</MenuItem>
                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                        <MenuItem value="QUARTERLY">Quarterly (every 3 months)</MenuItem>
                        <MenuItem value="HALF_YEARLY">Half Yearly (every 6 months)</MenuItem>
                        <MenuItem value="YEARLY">Yearly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={!dvlaCheckFrequency}>
                      <InputLabel>Day of Month</InputLabel>
                      <Select
                        value={dvlaScheduleDayOfMonth}
                        onChange={(e) => setDvlaScheduleDayOfMonth(Number(e.target.value))}
                        label="Day of Month"
                      >
                        {DVLA_DAY_OF_MONTH_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Time of Day"
                      type="time"
                      value={dvlaScheduleTime}
                      onChange={(e) => setDvlaScheduleTime(e.target.value)}
                      disabled={!dvlaCheckFrequency}
                      InputLabelProps={{ shrink: true }}
                      helperText="Local time in the selected timezone"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={!dvlaCheckFrequency}>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={dvlaScheduleTimezone}
                        onChange={(e) => setDvlaScheduleTimezone(e.target.value)}
                        label="Timezone"
                      >
                        {DVLA_TIMEZONE_OPTIONS.map((tz) => (
                          <MenuItem key={tz} value={tz}>
                            {tz.replace(/_/g, ' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                {dvlaCheckFrequency && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {formatScheduleSummary({
                      frequency: dvlaCheckFrequency,
                      dayOfMonth: dvlaScheduleDayOfMonth,
                      time: dvlaScheduleTime,
                      timezone: dvlaScheduleTimezone,
                      nextCheckDate: nextDvlaCheckDate,
                    })}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Legacy DVLA notification email
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Guaranteed recipient for automated check summary reports and immediate &quot;DVLA Alert&quot; emails
                  when a manual check finds issues. Works alongside Communications → Alerts — both can be active at once.
                  Comma-separated addresses are supported.
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  label="DVLA notification email(s)"
                  value={dvlaAutomatedCheckEmail}
                  onChange={(e) => setDvlaAutomatedCheckEmail(e.target.value)}
                  placeholder="dvla-reports@example.com, fleet@example.com"
                  helperText="Leave empty to skip legacy emails (Communications → Alerts rules still apply)"
                />
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      DVLA Email & Push Alerts
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Configure flexible per-event rules in <strong>Communications → Alerts</strong> for licence/photocard
                      expiry, points changes, new endorsements and status changes — targeting all drivers or specific staff,
                      with delivery to users, teams, tags or external email addresses.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use the legacy email above for a fixed fallback; use Communications → Alerts for targeted rules and push.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/admin/communications')}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Manage alerts
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Test Automated Check
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Run a test automated check for all staff members in this business. This will consume 1 credit per
                  staff member checked. A legacy summary email is sent to the address above (if set). Per-event email
                  and push also fire via any matching rules in <strong>Communications → Alerts</strong>.
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={async () => {
                    if (!businessId) {
                      alert('Business ID not found');
                      return;
                    }
                    
                    const confirmMessage = `This will run DVLA checks for all staff members in this business and consume 1 credit per staff member checked.\n\n` +
                      `Legacy summary email: ${dvlaAutomatedCheckEmail.trim() || 'not configured'}\n` +
                      `Per-event alerts: via Communications → Alerts rules\n\n` +
                      `Do you want to continue?`;
                    
                    if (!window.confirm(confirmMessage)) return;
                    
                    try {
                      setLoading(true);
                      setError(null);
                      const token = localStorage.getItem('token');
                      if (!token) { 
                        navigate('/login'); 
                        return; 
                      }
                      
                      const response = await axios.post(
                        `${API_BASE_URL}/dvla-staff/trigger-automated-check/${businessId}`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      
                      const result = response.data.result || {};
                      const checksPerformed = result.checksPerformed || 0;
                      const creditsUsed = result.creditsUsed || 0;
                      const emailSent = result.emailSent || false;

                      setSaveSuccess(true);
                      setTimeout(() => setSaveSuccess(false), 5000);

                      const successMessage = `✅ Test automated check completed!\n\n` +
                        `Checks performed: ${checksPerformed}\n` +
                        `Credits used: ${creditsUsed}\n` +
                        `Legacy summary email: ${emailSent ? 'Yes' : 'No'}${emailSent && dvlaAutomatedCheckEmail.trim() ? ` (to ${dvlaAutomatedCheckEmail.trim()})` : ''}\n\n` +
                        `Per-event alerts (if any matched) were also delivered via Communications → Alerts.\n\n` +
                        `${result.message || 'Check completed successfully'}`;
                      
                      alert(successMessage);
                    } catch (err: any) {
                      console.error('Error running test automated check:', err);
                      const errorMessage = err.response?.data?.message || 
                                          err.response?.data?.error || 
                                          err.response?.data?.details ||
                                          'Failed to run test automated check';
                      setError(errorMessage);
                      alert(`❌ Error: ${errorMessage}`);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  sx={{ mt: 1 }}
                >
                  {loading ? 'Running Test...' : 'Run Test Automated Check'}
                </Button>
              </CardContent>
            </Card>

            {/* Sender Email Mappings Section */}
            <Divider sx={{ my: 4 }} />
            <Box sx={{ mt: 4 }}>
              <SenderEmailMappings 
                businessId={businessId || ''} 
                businessName={businessName}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveIntegrationSettings}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Integration Settings'}
              </Button>
            </Box>
          </TabPanel>

          {/* Email Configuration Tab */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>
              Email Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter comma-separated email addresses for each field.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Emails (comma-separated)"
                  value={accountEmails}
                  onChange={(e) => setAccountEmails(e.target.value)}
                  margin="normal"
                  placeholder="accounts@acme.com, billing@acme.com"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Overview Emails (comma-separated)"
                  value={overviewEmails}
                  onChange={(e) => setOverviewEmails(e.target.value)}
                  margin="normal"
                  placeholder="overview@acme.com, ops@acme.com"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      if (!businessId) return;
                      try {
                        setLoading(true);
                        setSaveSuccess(false);
                        const token = localStorage.getItem('token');
                        if (!token) { navigate('/login'); return; }
                        await axios.put(
                          `${API_BASE_URL}/businesses/${businessId}/email-settings`,
                          {
                            account_emails: accountEmails.trim() || null,
                            overview_emails: overviewEmails.trim() || null,
                          },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setSaveSuccess(true);
                        setTimeout(() => setSaveSuccess(false), 3000);
                      } catch (err) {
                        console.error('Error saving email settings:', err);
                        setError('Failed to save email settings.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Import History Tab */}
          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" gutterBottom>
              Asset Import History
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View and manage all asset imports for this business. You can undo imports to delete all assets that were created in that import.
            </Typography>

            {loadingImports ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : importHistory.length === 0 ? (
              <Alert severity="info">
                No import history found for this business.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Imported By</TableCell>
                      <TableCell>Total Rows</TableCell>
                      <TableCell>Successful</TableCell>
                      <TableCell>Failed</TableCell>
                      <TableCell>Skipped</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importHistory.map((importRecord) => (
                      <TableRow key={importRecord.import_id}>
                        <TableCell>
                          {new Date(importRecord.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {importRecord.user_first_name} {importRecord.user_last_name}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {importRecord.user_email}
                          </Typography>
                        </TableCell>
                        <TableCell>{importRecord.total_rows}</TableCell>
                        <TableCell>
                          <Chip 
                            label={importRecord.successful_imports} 
                            color="success" 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          {importRecord.failed_imports > 0 ? (
                            <Chip 
                              label={importRecord.failed_imports} 
                              color="error" 
                              size="small" 
                            />
                          ) : (
                            importRecord.failed_imports
                          )}
                        </TableCell>
                        <TableCell>{importRecord.skipped_imports}</TableCell>
                        <TableCell>
                          {importRecord.is_undone ? (
                            <Box>
                              <Chip 
                                label="Undone" 
                                color="default" 
                                size="small" 
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(importRecord.undone_at).toLocaleString()}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                By: {importRecord.undone_by_first_name} {importRecord.undone_by_last_name}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip 
                              label="Active" 
                              color="primary" 
                              size="small" 
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {!importRecord.is_undone && importRecord.successful_imports > 0 && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleUndoImport(importRecord)}
                            >
                              Undo Import
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Copy setup from another business – add templates & asset types (e.g. Check-Now Ltd (Demo)) */}
          <TabPanel value={tabValue} index={6}>
            <Typography variant="h6" gutterBottom>
              Add templates &amp; asset types to this business
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Copy check templates and asset types from another business into this one. Use <strong>Check-Now Ltd (Demo)</strong> to add the standard templates and asset types. Tick the ones you want to add, then click Copy from source.
            </Typography>
            {setupFromSourceSuccess && setupFromSourceCounts && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Added {setupFromSourceCounts.assetTypes} asset type(s) and {setupFromSourceCounts.templates} template(s) to this business.
              </Alert>
            )}
            {setupFromSourceError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSetupFromSourceError(null)}>
                {setupFromSourceError}
              </Alert>
            )}
            {loadingBusinessesForCopy ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="copy-source-business-label">Source business</InputLabel>
                    <Select
                      labelId="copy-source-business-label"
                      value={sourceBusinessIdForCopy}
                      label="Source business"
                      onChange={(e) => setSourceBusinessIdForCopy(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select a business</em>
                      </MenuItem>
                      {businessesList
                        .filter((b) => b.business_id !== businessId)
                        .map((b) => (
                          <MenuItem key={b.business_id} value={b.business_id}>
                            {b.business_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                {sourceBusinessIdForCopy && (
                  <>
                    {loadingSourceDataForCopy ? (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">Loading templates and asset types...</Typography>
                        </Box>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Templates to add</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 0 }}>
                            {sourceTemplatesForCopy.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No templates in this business.</Typography>
                            ) : (
                              sourceTemplatesForCopy.map((t) => (
                                <FormControlLabel
                                  key={t.template_id}
                                  control={
                                    <Checkbox
                                      checked={selectedTemplateIdsForCopy.includes(t.template_id)}
                                      onChange={(e) => {
                                        if (e.target.checked) setSelectedTemplateIdsForCopy((prev) => [...prev, t.template_id]);
                                        else setSelectedTemplateIdsForCopy((prev) => prev.filter((id) => id !== t.template_id));
                                      }}
                                    />
                                  }
                                  label={t.template_name}
                                />
                              ))
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Asset types to add</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 0 }}>
                            {sourceAssetTypesForCopy.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No asset types in this business.</Typography>
                            ) : (
                              sourceAssetTypesForCopy.map((a) => (
                                <FormControlLabel
                                  key={a.asset_type_id}
                                  control={
                                    <Checkbox
                                      checked={selectedAssetTypeIdsForCopy.includes(a.asset_type_id)}
                                      onChange={(e) => {
                                        if (e.target.checked) setSelectedAssetTypeIdsForCopy((prev) => [...prev, a.asset_type_id]);
                                        else setSelectedAssetTypeIdsForCopy((prev) => prev.filter((id) => id !== a.asset_type_id));
                                      }}
                                    />
                                  }
                                  label={a.type_name}
                                />
                              ))
                            )}
                          </Box>
                        </Grid>
                        {(selectedTemplateIdsForCopy.length > 0 || selectedAssetTypeIdsForCopy.length > 0) && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {selectedTemplateIdsForCopy.length} template(s) and {selectedAssetTypeIdsForCopy.length} asset type(s) selected.
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCopyFromSource}
                    disabled={setupFromSourceLoading || !sourceBusinessIdForCopy || (selectedTemplateIdsForCopy.length === 0 && selectedAssetTypeIdsForCopy.length === 0)}
                  >
                    {setupFromSourceLoading ? 'Copying...' : 'Copy from source'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={7}>
            <BusinessCleanupPanel
              businessId={businessId || ''}
              businessName={businessName || business?.business_name || ''}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={8}>
            {businessId && (
              <AdminUiLabelsPanel
                businessId={businessId}
                adminUiLabelsRaw={business?.admin_ui_labels}
                onSaved={fetchBusinessDetails}
              />
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={9}>
            {businessId && (
              <StorageUsagePanel businessId={businessId} />
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={10}>
            <AssetViewsSettings />
          </TabPanel>

          <TabPanel value={tabValue} index={11}>
            <LocationViewsSettings />
          </TabPanel>
        </Paper>
      )}

      {/* Undo Import Confirmation Dialog */}
      <Dialog
        open={undoDialogOpen}
        onClose={() => !undoingImport && setUndoDialogOpen(false)}
      >
        <DialogTitle>Confirm Undo Import</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to undo this import?
            <br /><br />
            <strong>This will permanently delete {selectedImport?.successful_imports} assets</strong> that were created in this import.
            <br /><br />
            Import Details:
            <ul>
              <li>Date: {selectedImport && new Date(selectedImport.created_at).toLocaleString()}</li>
              <li>Imported by: {selectedImport?.user_first_name} {selectedImport?.user_last_name}</li>
              <li>Assets to delete: {selectedImport?.successful_imports}</li>
            </ul>
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUndoDialogOpen(false)} 
            disabled={undoingImport}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmUndoImport} 
            color="error" 
            variant="contained"
            disabled={undoingImport}
          >
            {undoingImport ? <CircularProgress size={24} /> : 'Yes, Undo Import'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Auto-save Success Snackbar */}
      <Snackbar
        open={autoSaveSuccess}
        message="DVLA credits saved automatically ✓"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'success.main',
            color: 'success.contrastText',
          }
        }}
      />
    </Container>
  );
};

export default BusinessSettings; 